import React from 'react';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { PlanProgramStatus } from '@src/constants/planProgramStatus';
import { ShimmerLoader } from '@guildeducationinc/recess';
import { useDecision } from '@optimizely/react-sdk';
import { NOTIFY_ME_WHEN } from '@src/constants/featureFlags';
import { usePricebook } from '@src/components/Providers/PricebookProvider';
import { CompletionTimeHighlight } from './CompletionTimeHighlight';
import { EffortHighlight } from './EffortHighlight';
import { ProgramFormatHighlight } from './ProgramFormatHighlight';
import { ProgramKindHighlight } from './ProgramKindHighlight';
import styles from './ProgramHighlightsPanel.module.scss';
import { DeferredDateCta } from './DateCta/DeferredDateCta';
import { StandardDateCta } from './DateCta/StandardDateCta';
import { FundingHighlight } from './FundingHighlight/FundingHighlight';
import { EligibilityHighlightPanel } from './EligibilityHighlightPanel';
import { CappedDateCta } from './DateCta/CappedDateCta/CappedDateCta';
import { CappedDateCtaNotifyMe } from './DateCta/CappedDateCta';
import { CostHighlight } from './FundingHighlight/CostHighlight';

export interface ProgramHighlightProps {
	isSticky?: boolean;
	loading: boolean;
	program: GetMemberProgramQuery['memberProgram'];
}

export const ProgramHighlightsPanel: React.FC<ProgramHighlightProps> = ({
	isSticky,
	loading,
	program,
}) => {
	const [notifyMeWhenDecision] = useDecision(NOTIFY_ME_WHEN);
	const { showCost } = usePricebook();
	const { memberPlanData } = useSearchContext();
	const eligible = memberPlanData.eligibility.eligible;

	const getDateCta = () => {
		if (program.planProgramStatus === PlanProgramStatus.DEFERRED) {
			return <DeferredDateCta program={program} />;
		}

		if (program.planProgramStatus === PlanProgramStatus.CAPPED) {
			if (notifyMeWhenDecision.enabled) {
				return <CappedDateCtaNotifyMe programId={program.id} />;
			}
			return <CappedDateCta />;
		}

		return <StandardDateCta loading={loading} program={program} />;
	};

	return (
		<>
			{!eligible && !isSticky ? <EligibilityHighlightPanel programUuid={program.uuid} /> : null}

			<div className={styles.highlightsContainer}>
				{showCost ? (
					<CostHighlight loading={loading} program={program} />
				) : (
					<FundingHighlight loading={loading} program={program} />
				)}
				<ProgramKindHighlight loading={loading} program={program} />
				<ProgramFormatHighlight loading={loading} program={program} />
				<CompletionTimeHighlight loading={loading} program={program} />
				<EffortHighlight loading={loading} program={program} />
			</div>

			<div className={styles.dateCtaContainer}>
				<ShimmerLoader label='Date information' loaded={!loading}>
					{getDateCta()}
				</ShimmerLoader>
			</div>
		</>
	);
};
